import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";

const Overlay = styled.div`
  position: fixed;
  top: 68px;
  left: 0;
  width: 100%;
  height: calc(100% - 80px);
  background: rgba(0, 0, 0, 0.5);
  z-index: 40;
  opacity: ${(props) => (props.isOpen ? 1 : 0)};
  visibility: ${(props) => (props.isOpen ? "visible" : "hidden")};
  transition: all 0.3s ease;
`;

const Sidebar = styled.div`
  position: fixed;
  top: 68px;
  left: 0;
  width: 100%;
  height: calc(100% - 80px);
  background: white;
  z-index: 45;
  transform: translateX(${(props) => (props.isOpen ? "0" : "-100%")});
  transition: transform 0.3s ease;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 320px) {
    width: 280px;
  }
`;

const PanelContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const Panel = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: white;
  transform: translateX(
    ${(props) => {
      if (props.level === 0) return props.activePanel === null ? "0" : "-100%";
      return props.activePanel === props.panelKey ? "0" : "100%";
    }}
  );
  transition: transform 0.3s ease;
  display: flex;
  flex-direction: column;
`;

const PanelHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #e5e5e5;
  display: flex;
  align-items: center;
  gap: 15px;
  background: #f8f8f8;
`;

const BackButton = styled.button`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: #333;
  border-radius: 4px;

  &:hover {
    background: #e5e5e5;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const PanelTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  background: white;
`;

const NavSection = styled.div`
  padding: 20px 0;
`;

const NavList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const NavItem = styled.li``;

const NavLink = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  color: #333;
  font-size: 15px;
  font-weight: 500;
  border-bottom: 1px solid #f0f0f0;
  width: 100%;
  background: transparent;
  border: none;
  text-align: left;

  &:hover {
    background: #f8f8f8;
    color: #ff6720;
  }

  svg {
    width: 12px;
    height: 12px;
    color: #999;
    transition: transform 0.3s ease;
  }
`;

const SubNavList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const SubNavItem = styled.li``;

const SubNavLink = styled(Link)`
  display: block;
  padding: 16px 20px;
  color: #333;
  font-size: 15px;
  font-weight: 400;
  border-bottom: 1px solid #f0f0f0;

  &:hover {
    background: #f8f8f8;
    color: #ff6720;
  }
`;

const ShopAllLink = styled(Link)`
  display: block;
  padding: 16px 20px;
  color: #ff6720;
  font-size: 15px;
  font-weight: 600;
  border-bottom: 1px solid #f0f0f0;
  background: #fff5f0;

  &:hover {
    background: #ffe5d6;
  }
`;


const categories = {
  chainsaws: [
    {
      name: "Petrol Chainsaws",
      path: "/collections/chainsaws?type=petrol",
    },
    { name: "Gardening + Planting", path: "/collections/gardening" },
    { name: "Lawn Mowers", path: "/collections/lawn-mowers" },
    { name: "Leaf Blowers", path: "/collections/leaf-blowers" },
    { name: "String Trimmers", path: "/collections/trimmers-weed-wackers" },
    { name: "Hedge Trimmers", path: "/collections/hedge-trimmers" },
    { name: "Pressure Washers", path: "/collections/pressure-washers" },
    { name: "Chainsaws", path: "/collections/chainsaws" },
  ],
  brushcutter: [
    {
      name: "Petrol Brushcutters",
      path: "/collections/brushcutters?type=petrol",
    },
    { name: "Electric Drills", path: "/collections/electric-drills" },
    { name: "Impact Driver Drills", path: "/collections/impact-drivers" },
    { name: "Hammer Drills", path: "/collections/hammer-drills" },
    { name: "Sanders", path: "/collections/sander-polishers" },
    { name: "Saws", path: "/collections/circular-saws" },
    { name: "Tool Sets", path: "/collections/tools-set" },
  ],
  "Brushcutter": [
    {
      name: "Measure + Layout Tools",
      path: "/collections/measure-layout-tools",
    },
    { name: "Stud Finders", path: "/collections/stud-finders" },
    { name: "Tape Measures", path: "/collections/tape-measures" },
    { name: "Tool Sets", path: "/collections/tools-set" },
    { name: "Screwdrivers", path: "/collections/screwdrivers" },
  ],
  vacuums: [
    { name: "Handheld Vacuums", path: "/collections/handheld-vacuums" },
    { name: "Cordless Vacuums", path: "/collections/cordless-vacuums" },
    { name: "Car Vacuums", path: "/collections/car-vacuums" },
    { name: "Stick Vacuums", path: "/collections/powerseries-vacuum-cleaners" },
    { name: "Pet Vacuums", path: "/collections/pet-vacuums" },
  ],
};

const categoryTitles = {
  chainsaws: "Chainsaws",
  brushcutter: "Brushcutters",
  "hand-tools": "Brushcutter",
  vacuums: "Vacuums",
};

const MobileMenu = ({ isOpen, onClose }) => {
  const [activePanel, setActivePanel] = useState(null);

  const openCategoryPanel = (category) => {
    setActivePanel(category);
  };

  const goBackToMain = () => {
    setActivePanel(null);
  };

  const handleClose = () => {
    setActivePanel(null);
    onClose();
  };

  const handleLinkClick = () => {
    handleClose();
  };

  // Prevent background scrolling when menu is open
  useEffect(() => {
    if (isOpen) {
      // Prevent scrolling
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.top = `-${window.scrollY}px`;
      document.body.style.width = "100%";
    } else {
      // Restore scrolling
      const scrollY = document.body.style.top;
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || "0") * -1);
      }
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
    };
  }, [isOpen]);

  return (
    <>
      <Overlay isOpen={isOpen} onClick={handleClose} />
      <Sidebar isOpen={isOpen}>
        <PanelContainer>
          {/* Main Panel */}
          <Panel level={0} activePanel={activePanel}>
            <Content>
              <NavSection>
                <NavList>
                  <NavItem>
                    <NavLink onClick={() => openCategoryPanel("chainsaws")}>
                      Chainsaws
                      <svg fill="none" viewBox="0 0 6 4">
                        <path
                          d="m.884 3.478 2.52-2.178L5.785 3.48"
                          stroke="currentColor"
                          strokeWidth=".545"
                        />
                      </svg>
                    </NavLink>
                  </NavItem>

                  <NavItem>
                    <NavLink onClick={() => openCategoryPanel("brushcutter")}>
                      Brushcutters
                      <svg fill="none" viewBox="0 0 6 4">
                        <path
                          d="m.884 3.478 2.52-2.178L5.785 3.48"
                          stroke="currentColor"
                          strokeWidth=".545"
                        />
                      </svg>
                    </NavLink>
                  </NavItem>

                  <NavItem>
                    <NavLink onClick={() => openCategoryPanel("hand-tools")}>
                      Hand Tools
                      <svg fill="none" viewBox="0 0 6 4">
                        <path
                          d="m.884 3.478 2.52-2.178L5.785 3.48"
                          stroke="currentColor"
                          strokeWidth=".545"
                        />
                      </svg>
                    </NavLink>
                  </NavItem>

                  <NavItem>
                    <NavLink onClick={() => openCategoryPanel("vacuums")}>
                      Vacuums
                      <svg fill="none" viewBox="0 0 6 4">
                        <path
                          d="m.884 3.478 2.52-2.178L5.785 3.48"
                          stroke="currentColor"
                          strokeWidth=".545"
                        />
                      </svg>
                    </NavLink>
                  </NavItem>
                </NavList>
              </NavSection>
            </Content>
            
          </Panel>

          {/* Category Panels */}
          {Object.keys(categories).map((categoryKey) => (
            <Panel
              key={categoryKey}
              level={1}
              panelKey={categoryKey}
              activePanel={activePanel}
            >
              <PanelHeader>
                <BackButton onClick={goBackToMain}>
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.42-1.41L7.83 13H20v-2z" />
                  </svg>
                </BackButton>
                <PanelTitle>{categoryTitles[categoryKey]}</PanelTitle>
              </PanelHeader>

              <Content>
                <SubNavList>
                  <SubNavItem>
                    <ShopAllLink
                      to={`/collections/${categoryKey}`}
                      onClick={handleLinkClick}
                    >
                      Shop All {categoryTitles[categoryKey]}
                    </ShopAllLink>
                  </SubNavItem>
                  {categories[categoryKey].map((item, index) => (
                    <SubNavItem key={index}>
                      <SubNavLink to={item.path} onClick={handleLinkClick}>
                        {item.name}
                      </SubNavLink>
                    </SubNavItem>
                  ))}
                </SubNavList>
              </Content>
            </Panel>
          ))}
        </PanelContainer>
      </Sidebar>
    </>
  );
};

export default MobileMenu;
